/* ============================================================
 * src/lib/format.ts — Pure presentation formatters (FR-001 / WIRE-0).
 *
 * Extracted verbatim from src/mock/circa.ts (which previously
 * exposed the four formatters and three label maps via the
 * `CIRCA` mock object). These are pure, side-effect-free, and
 * shared by both mock-backed and Convex-wired screens, so they
 * must outlive the eventual deletion of the mock fixture.
 *
 * Contract is pinned by frontend-v3/tests/unit/format.test.ts
 * (T300). PRD 1C.3 invariants:
 *   - fmtDur(3_661_000) === '1h 1m'
 *   - fmtClock(3_661_000) === '01:01:01'
 *   - ago(null) === '—' (U+2014)
 *   - money uses 'es-MX' locale with min/max fraction digits 2.
 *
 * Spanish operator copy (PRD §3 — UI strings in Spanish).
 * ============================================================ */

export function fmtDur(ms: number): string {
  const s = Math.round(ms / 1000); const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const ss = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${ss}s`;
  return `${ss}s`;
}

export function fmtClock(ms: number): string {
  const s = Math.round(ms / 1000); const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0'); const ss = String(s % 60).padStart(2, '0');
  return `${h}:${m}:${ss}`;
}

export function ago(ts: number | null): string {
  if (!ts) return '—';
  const d = Date.now() - ts; const m = Math.round(d / 60000);
  if (m < 1) return 'hace un momento';
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60); if (h < 24) return `hace ${h} h`;
  const days = Math.round(h / 24); return `hace ${days} d`;
}

export function money(n: number): string {
  return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const statusLabels: Record<string, string> = {
  live: 'En vivo',
  idle: 'Inactivo',
  error: 'Error',
  starting: 'Iniciando',
  stopping: 'Deteniendo',
  adbreak: 'Corte publicitario',
  ready: 'Listo',
  processing: 'Procesando',
  uploading: 'Subiendo',
  draft: 'Borrador',
  scheduled: 'Programado',
  completed: 'Completado',
  archived: 'Archivado',
  active: 'Activo',
  invited: 'Invitado',
  suspended: 'Suspendido',
  provisioning: 'Aprovisionando',
  failed: 'Fallido',
};

export const sevLabels: Record<string, string> = {
  critical: 'Crítico',
  error: 'Error',
  warning: 'Advertencia',
  info: 'Información',
};

export const roleLabels: Record<string, string> = {
  admin: 'Administrador',
  editor: 'Editor',
  viewer: 'Lector',
};
